import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, X, Crown, Sparkles, Building2, ArrowRight, Flame, Zap, Lock, Award, ShieldCheck, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUBSTACK_MATH = [
  { k: "1,000 paid subs at $5/mo each", v: "$5,000/mo" },
  { k: "Your Vitality cost (Mogul, annual)", v: "$332/mo" },
  { k: "Net cash from one brand", v: "$4,668/mo" },
  { k: "Multiply by 5 client brands (Mogul)", v: "$23,340/mo" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [interval, setInterval] = useState("annual"); // monthly | annual
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.message("No charge made. Come back when you're ready.");
    }
    if (user) {
      api.billingConfig().then(setConfig).catch(() => {});
    } else {
      // public: still show pricing with no live counts
      setConfig({
        tiers: [
          { id: "operator", name: "Operator", subtitle: "For solo creators serious about a full year of viral content",
            monthly_amount: 14900, annual_amount: 149000, currency: "usd", type: "subscription",
            features: ["3 brands (own or client)", "Unlimited AI writing (Claude Sonnet 4.5)", "One-click year of viral content", "All 13 platforms — auto-adapted", "Premium AI cover art", "Whisper video/audio repurposing", "Autopilot scheduler", "Live Substack preview", "Email & priority chat support"],
            best_for: "Founders & solo creators ready to publish like a media company" },
          { id: "mogul", name: "Mogul", subtitle: "For agencies, ghostwriters, and creators with clients",
            monthly_amount: 39900, annual_amount: 399000, currency: "usd", type: "subscription", popular: true,
            features: ["UNLIMITED brands (owned + client)", "Brand cross-pollination engine", "White-label sender personas", "Affiliate dashboard + 40% recurring", "Priority generation queue", "Concierge onboarding", "Multi-workspace collaboration", "All Operator features", "Dedicated success manager"],
            best_for: "Agencies & ghostwriters running 5+ client brands" },
          { id: "founding", name: "Founding Mogul", subtitle: "Lifetime access. First 50 only.",
            monthly_amount: 299700, annual_amount: null, currency: "usd", type: "one_time",
            features: ["Everything in Mogul — FOREVER", "Lifetime price lock", "Founders Slack + Q&A", "Name on the founders wall", "First access to every feature", "Double affiliate rate: 50%", "1-on-1 setup call with creator", "Annual brand audit"],
            best_for: "True believers who want upside locked in" },
        ],
        founding_remaining: 50,
        founding_slots: 50,
        active_subscribers: 0,
      });
    }
  }, [user, searchParams]);

  const handleCheckout = async (tier, type) => {
    if (!user) {
      const ref = searchParams.get("ref") || localStorage.getItem("vit_ref");
      if (ref) localStorage.setItem("vit_ref", ref);
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard?intent=billing&tier=" + tier + "&interval=" + interval)}`;
      return;
    }
    setBusy(`${tier}-${type}`);
    try {
      const realInterval = type === "one_time" ? "lifetime" : interval;
      const { url } = await api.createCheckout(tier, realInterval, window.location.origin);
      if (url) window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setBusy(null);
    }
  };

  if (!config) return <div className="min-h-screen flex items-center justify-center text-sm mono uppercase text-[#A1A1AA]">loading pricing…</div>;

  const tiers = config.tiers;
  const founding = tiers.find((t) => t.id === "founding");
  const sold = config.founding_slots - config.founding_remaining;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <header className="flex items-center justify-between mb-12">
          <button onClick={() => navigate(user ? "/dashboard" : "/")} className="flex items-center gap-2" data-testid="pricing-logo">
            <div className="w-6 h-6 bg-black" />
            <span className="font-display font-black tracking-tighter">VITALITY/<span className="text-[#002FA7]">SS</span></span>
          </button>
          {!user && (
            <button onClick={() => handleCheckout("operator", "subscription")} className="text-eyebrow hover:text-[#0A0A0B]" data-testid="pricing-signin">
              Sign in →
            </button>
          )}
        </header>

        {/* HERO */}
        <div className="max-w-4xl mb-10">
          <div className="text-eyebrow mb-4">Premium. Built for movers.</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]"
          >
            $0 audience.<br/>
            <span className="text-[#002FA7]">$0 platforms.</span><br/>
            $100,000+ Substack ARR within a year.
          </motion.h1>
          <p className="mt-6 text-lg text-[#52525B] leading-relaxed max-w-3xl">
            This is not for hobbyists. It's for the operators turning a Substack into a media business. <strong className="text-black">Zero audience required — on any platform.</strong> Start from nothing, on any brand, even client brands at zero. The system writes, schedules, and ships a year of viral content while you sleep.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 text-sm border border-black px-3 py-1.5">
              <ShieldCheck size={14} /> 7-day full-feature trial — but only for serious operators
            </div>
            <div className="flex items-center gap-2 text-sm border border-black px-3 py-1.5">
              <Lock size={14} /> Cancel anytime. No questions.
            </div>
          </div>
        </div>

        {/* INTERVAL TOGGLE */}
        <div className="inline-flex border border-black mb-10" data-testid="interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            data-testid="interval-monthly"
            className={`px-5 py-2 text-sm tracking-wide ${interval === "monthly" ? "bg-black text-white" : "bg-white text-black hover:bg-[#F4F5F6]"}`}
          >Monthly</button>
          <button
            onClick={() => setInterval("annual")}
            data-testid="interval-annual"
            className={`px-5 py-2 text-sm tracking-wide flex items-center gap-2 ${interval === "annual" ? "bg-black text-white" : "bg-white text-black hover:bg-[#F4F5F6]"}`}
          >
            Annual <span className={`text-xs px-1.5 py-0.5 ${interval === "annual" ? "bg-[#F59E0B] text-black" : "bg-[#F59E0B]/10 text-[#B45309]"}`}>SAVE 2 MO</span>
          </button>
        </div>

        {/* TIERS */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {tiers.map((t) => {
            const isOneTime = t.type === "one_time";
            const amount = isOneTime ? t.monthly_amount : (interval === "monthly" ? t.monthly_amount : t.annual_amount);
            const popular = t.popular;
            const remaining = t.id === "founding" ? config.founding_remaining : null;
            const soldOut = t.id === "founding" && remaining <= 0;
            return (
              <div
                key={t.id}
                className={`border-2 ${popular ? "border-black brutal-shadow-lg" : "border-[#E2E4E9]"} bg-white p-6 relative flex flex-col`}
                data-testid={`tier-card-${t.id}`}
              >
                {popular && (
                  <div className="absolute -top-3 left-6 bg-[#F59E0B] text-black text-eyebrow px-2 py-1">
                    MOST POPULAR
                  </div>
                )}
                {t.id === "founding" && (
                  <div className="absolute -top-3 right-6 bg-black text-white text-eyebrow px-2 py-1 flex items-center gap-1">
                    <Flame size={12}/> {sold}/{config.founding_slots} SOLD
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  {t.id === "operator" && <Zap size={20} />}
                  {t.id === "mogul" && <Building2 size={20} />}
                  {t.id === "founding" && <Crown size={20} className="text-[#F59E0B]" />}
                  <div className="font-display font-bold text-2xl tracking-tight">{t.name}</div>
                </div>
                <p className="text-xs text-[#52525B] mb-5 leading-relaxed">{t.subtitle}</p>

                <div className="mb-1">
                  <span className="font-display font-black text-5xl tracking-tighter">${(amount / 100).toLocaleString()}</span>
                  <span className="text-sm text-[#52525B] ml-1">
                    {isOneTime ? "one-time · forever" : (interval === "monthly" ? "/mo" : "/yr")}
                  </span>
                </div>
                {!isOneTime && interval === "annual" && (
                  <p className="text-xs text-[#059669] mono mb-4">${(amount / 12 / 100).toFixed(0)}/mo effective · 2 months free</p>
                )}
                {!isOneTime && interval === "monthly" && (
                  <p className="text-xs mono text-[#A1A1AA] mb-4">${((amount * 12) / 100).toLocaleString()}/yr if billed monthly</p>
                )}

                <button
                  onClick={() => handleCheckout(t.id, t.type)}
                  disabled={busy === `${t.id}-${t.type}` || soldOut}
                  data-testid={`subscribe-${t.id}`}
                  className={`mt-2 mb-6 w-full py-3.5 text-sm tracking-wide flex items-center justify-center gap-2 transition-colors ${
                    soldOut ? "bg-[#A1A1AA] text-white cursor-not-allowed" :
                    popular ? "bg-black text-white hover:bg-[#002FA7]" :
                    "bg-white text-black border-2 border-black hover:bg-black hover:text-white"
                  }`}
                >
                  {soldOut ? "SOLD OUT" : busy === `${t.id}-${t.type}` ? "Loading…" : (
                    <>{isOneTime ? "Claim a slot" : "Take my money"} <ArrowRight size={16} /></>
                  )}
                </button>

                <div className="text-eyebrow mb-2">{t.best_for}</div>
                <ul className="space-y-2 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className="text-[#059669] shrink-0 mt-1" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* WHAT YOU BRING / WHAT WE AUTOMATE */}
        <div className="bg-[#FAFAFA] border border-black p-8 md:p-12 mb-16">
          <div className="text-eyebrow mb-3">Transparency you'll never get from Big SaaS</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-8">What you actually do vs what we automate.</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-sm mono uppercase tracking-wide text-[#0A0A0B] mb-3 flex items-center gap-2">YOU BRING</div>
              <ul className="space-y-3 text-sm">
                <Bring label="Your voice & permission" detail="One 10-minute interview, plus any raw material (PDFs, transcripts, photos, audio, video clips)." />
                <Bring label="A Substack account" detail="Free to create. We'll walk you through it in 60 seconds if you don't have one." />
                <Bring label="Review & approve" detail="15 minutes a week glancing at the approval queue — say yes, or reject with a note and we rewrite." />
                <Bring label="One 5-step Ayrshare wizard" detail="Once. Connects auto-publishing across 9 social platforms. ~10 minutes." />
              </ul>
            </div>
            <div>
              <div className="text-sm mono uppercase tracking-wide text-[#0A0A0B] mb-3">WE AUTOMATE</div>
              <ul className="space-y-3 text-sm">
                <Automate label="A year of Substack posts" detail="52 weekly drafts using proven viral playbooks (Hormozi, Welsh, Naval, contrarian, story→offer)." />
                <Automate label="Adapt to 13 platforms" detail="Instagram, X, LinkedIn, TikTok, YouTube, Pinterest, Threads, Facebook, Reddit, Medium, Substack Notes, Spotify, Apple Pods. Native formats, native hooks." />
                <Automate label="AI cover art per post" detail="Claude writes the art brief. Nano Banana renders. 6 style presets. Regenerate until you love it." />
                <Automate label="Auto-ship on schedule" detail="One toggle = autopilot. Substack drafts ready for 1-click publish via your authenticated session, plus every social platform via Ayrshare. You walk away." />
                <Automate label="Self-improving writer" detail="Top-performing past posts feed back into the prompt. The writer literally learns what works for YOUR voice." />
              </ul>
            </div>
          </div>
        </div>

        {/* THIRD PARTY COSTS */}
        <div className="border border-black p-8 mb-16">
          <h3 className="font-display font-black tracking-tighter text-2xl mb-2">The whole cost picture (we don't hide anything).</h3>
          <p className="text-sm text-[#52525B] mb-6">You'll need a few outside tools. Most have free tiers. None are required to START — only to scale to autopilot.</p>
          <div className="space-y-3 text-sm">
            <CostRow name="Substack publication" cost="FREE" note="Substack takes 10% only if/when you charge readers." />
            <CostRow name="Resend (Substack email delivery)" cost="$0 → $20/mo at 50k emails" note="Free up to 3,000 emails/mo. Pay only as you scale." />
            <CostRow name="Ayrshare (13-platform autopublish)" cost="$149-$349/mo" note="Optional. Without it, you can still copy-paste from the queue." />
            <CostRow name="Vitality (us)" cost={`$${interval === "annual" ? "332" : "399"}/mo (Mogul)`} note="What you pay to make all of this run on autopilot." />
          </div>
        </div>

        {/* ROI MATH */}
        <div className="bg-black text-white p-8 md:p-12 mb-16" data-testid="roi-math-section">
          <div className="text-eyebrow text-white/60 mb-3">ROI MATH · The numbers that make this insane</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-8">Substack's already proven the unit economics.</h2>
          <div className="grid md:grid-cols-2 gap-8 items-end">
            <div className="space-y-4">
              {SUBSTACK_MATH.map((r, i) => (
                <div key={i} className="flex items-baseline justify-between border-b border-white/20 pb-3">
                  <span className="text-sm text-white/70">{r.k}</span>
                  <span className="font-display font-black text-2xl tracking-tighter">{r.v}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-base text-white/80 leading-relaxed mb-6">
                You don't need an audience to start. The engine writes, ships, and helps you grow from zero — on every platform, for every brand you run.
              </p>
              <button onClick={() => handleCheckout("mogul", "subscription")} className="bg-[#F59E0B] text-black px-6 py-4 text-sm tracking-wide hover:bg-white hover:text-black transition-colors flex items-center gap-2" data-testid="roi-cta-button">
                Become a Mogul <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* AFFILIATE TEASER */}
        <div className="border-2 border-[#F59E0B] p-8 md:p-12 mb-16 bg-[#FFFBEB]">
          <div className="flex items-center gap-2 mb-3">
            <Award size={20} className="text-[#B45309]"/>
            <div className="text-eyebrow text-[#B45309]">Built-in commission engine</div>
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl mb-4">40% recurring. For life. On every customer you bring.</h2>
          <p className="text-sm leading-relaxed max-w-3xl mb-5">
            Every Mogul & Operator account gets a personal affiliate link. Every customer you bring, every month they stay subscribed — 40% lands in your payout balance. Founding Moguls get <strong>50%</strong>. Climb the tiers (Bronze → Silver → Gold → Diamond) to unlock swipe-file libraries, monthly office hours, and milestone cash bonuses.
          </p>
          <div className="grid md:grid-cols-4 gap-3 text-sm">
            <TierBox name="Bronze" range="1-5 refs" rate="40%" />
            <TierBox name="Silver" range="6-15 refs" rate="42% + swipes" />
            <TierBox name="Gold" range="16-49 refs" rate="45% + co-marketing" />
            <TierBox name="Diamond" range="50+ refs" rate="50% + $5K bonus" pop />
          </div>
        </div>

        {/* GUARANTEE / FOOTER */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="border border-black p-6">
            <ShieldCheck size={24} className="mb-3" />
            <div className="font-display font-bold text-xl mb-2">Sleep-easy guarantee.</div>
            <p className="text-sm text-[#52525B] leading-relaxed">Cancel any time from the customer portal. Annual plans are pro-rated. No "save offer" mind games. No retention call. Just out.</p>
          </div>
          <div className="border border-black p-6">
            <Mail size={24} className="mb-3" />
            <div className="font-display font-bold text-xl mb-2">A human if you need one.</div>
            <p className="text-sm text-[#52525B] leading-relaxed">Mogul gets a dedicated success manager. Founding Mogul gets a 1-on-1 setup call with the creator and annual brand audits.</p>
          </div>
        </div>

        <p className="text-center text-eyebrow text-[#A1A1AA]">© Vitality/SS · Premium for serious operators · No tire kickers</p>
      </div>
    </div>
  );
}

function Bring({ label, detail }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-4 h-4 border-2 border-black mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-[#52525B] leading-relaxed">{detail}</div>
      </div>
    </li>
  );
}

function Automate({ label, detail }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-4 h-4 bg-black mt-0.5 shrink-0 flex items-center justify-center">
        <Sparkles size={10} className="text-white" />
      </div>
      <div>
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-[#52525B] leading-relaxed">{detail}</div>
      </div>
    </li>
  );
}

function CostRow({ name, cost, note }) {
  return (
    <div className="grid grid-cols-12 gap-3 border-b border-[#E2E4E9] pb-3">
      <div className="col-span-4 font-semibold">{name}</div>
      <div className="col-span-3 mono">{cost}</div>
      <div className="col-span-5 text-xs text-[#52525B]">{note}</div>
    </div>
  );
}

function TierBox({ name, range, rate, pop }) {
  return (
    <div className={`border ${pop ? "border-black bg-white" : "border-[#E2E4E9] bg-white"} p-4`}>
      <div className={`text-eyebrow mb-1 ${pop ? "text-[#B45309]" : ""}`}>{name}</div>
      <div className="font-display font-bold text-lg">{rate}</div>
      <div className="text-xs text-[#52525B] mt-1">{range}</div>
    </div>
  );
}
